// pick() overload 1: only simple keys, not nested → exact Pick<T, Key>
export function pick<T extends Record<string, unknown>, Key extends keyof T>(
  obj: T,
  props: Key[]
): Pick<T, Key>;

// pick() overload 2: presence of dotted keys, nested -> fallback on Partial<T> for the rest of the props
export function pick<T extends Record<string, unknown>>(obj: T, props: string[]): Partial<T>;

/**
 * Picks a subset of properties from an object.
 * Supports dot-notation for nested picks (e.g. "address.city").
 *
 * @param obj - The source object to pick from
 * @param props - List of property keys to pick, supports dot-notation
 * @returns A new object containing only the picked properties
 */
export function pick<T extends Record<string, unknown>>(obj: T, props: string[]): Partial<T> {
  const picked: Record<string, unknown> = {};

  for (const prop of props) {
    if (prop.includes(".")) {
      const keys = prop.split(".");
      const topKey = keys[0];

      if (obj[topKey] === undefined || obj[topKey] === null) {
        continue; // Skip this property entirely
      }

      const nested = pick((obj[topKey] as Record<string, unknown>) ?? {}, [
        keys.slice(1).join("."),
      ]);

      picked[topKey] = {...((picked[topKey] as object) ?? {}), ...nested};
    } else {
      picked[prop] = obj[prop];
    }
  }

  return picked as Partial<T>;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

/**
 * Flattens a nested object into a single-level object using dot-notation keys.
 * Arrays and null values are treated as leaves and are not flattened.
 * @param obj - The object to flatten
 * @param [prefix] - Internal prefix used during recursion
 * @returns A new flat object with dot-notation keys
 */
export function flatten(obj: Record<string, unknown>, prefix = ""): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];

    if (isPlainObject(value)) {
      Object.assign(result, flatten(value, fullKey));
    } else {
      result[fullKey] = value;
    }
  }

  return result;
}

/**
 * Formats a person's full name.
 * Returns an empty string if the person is nullish or has no first name.
 * Omits the last name if it is empty or missing.
 *
 * @param [person] - The person object containing optional name fields
 * @returns The formatted name, or an empty string if unavailable
 */
export const personName = (person?: {firstName?: string; lastName?: string; customFields?: {poles?: string}}): string =>
  (person?.firstName?.length ?? 0) > 0
    ? (person?.lastName?.length ?? 0) > 0
      ? `${person!.firstName} ${person!.lastName}`
      : (person!.customFields?.poles?.length ?? 0) > 0 ? `${person!.firstName!} (${person!.customFields!.poles!})` : person!.firstName!
    : "";

/**
 * Removes duplicate values from an array, preserving first-occurrence order.
 * Returns undefined if the input is nullish.
 *
 * @param array - The array to deduplicate
 * @returns A new array with duplicates removed, or undefined
 */
export const removeDuplicates = <T>(array: T[]): T[] | undefined =>
  array?.filter((v, i, a) => a.indexOf(v) === i);

export const dotStringToArray = (str: string): Array<string> => str.split(".");
